import React, { useState } from 'react';

import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { login } from 'api/endpoints/auth';
import Button from 'components/Button';
import ErrorMessage from 'components/ErrorMessage';
import Input from 'components/Input';
import LOCAL_STORAGE_KEYS from 'constants/localStorageKeys';
import { MESSAGE_COMMON, MESSAGE_LOGIN } from 'constants/string/messages';
import { TEXT_COMMON, TEXT_LOGIN } from 'constants/string/texts';
import { toastError, toastSuccess } from 'lib/toast';
import AppRoutes from 'routes/routes';
import { setIsAuthenticated } from 'store/actions/auth';
import { setLoading } from 'store/actions/common';
import { validateEmail, isFullWidth, validateJapanese } from 'utils/helper';
import { removeFromLocalStorage, saveToLocalStorage } from 'utils/localStorage';

const Login = () => {
  const dispatch = useDispatch();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailInValidJapan, setEmailInValidJapan] = useState(false);

  const initialValues = {
    email: '',
    password: '',
  };

  const form = useFormik({
    initialValues: initialValues,
    validateOnChange: isSubmitted,
    validateOnBlur: false,
    validate: (values) => {
      const errors = {};
      if (values.email && (isFullWidth(values.email) || validateJapanese(values.email))) {
        errors.email = true;
        setEmailInValidJapan(true);
      } else if (!values.email || !validateEmail(values.email)) {
        errors.email = true;
        setEmailInValidJapan(false);
      } else {
        setEmailInValidJapan(false);
      }
      if (!values.password) errors.password = true;
      setIsSubmitted(true);
      return errors;
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      dispatch(setLoading({ isOpen: true }));
      await login(values)
        .then((response) => {
          setIsLoading(false);
          dispatch(setIsAuthenticated(response.data.access_token));
          saveToLocalStorage(LOCAL_STORAGE_KEYS.TOKEN, response.data.access_token);
          dispatch(setLoading({ isOpen: false }));
          toastSuccess(response?.message);
        })
        .catch((error) => {
          setIsLoading(false);
          toastError(error.message);
          dispatch(setLoading({ isOpen: false }));
        });
    },
  });

  return (
    <main data-testid="login" id="login" className="flex-middle-center min-h-screen py-3">
      <form onSubmit={form.handleSubmit} className="p-6" aria-label="Login Form">
        <div className="flex items-end justify-center">
          <span className="tracking-smallest text-32 font-bold text-logo h-12 flex-middle-center">{TEXT_COMMON.LOGO}</span>
          <span className="ml-2 text-sm font-bold text-logo w-17.5 h-5.25 flex-middle-center mb-1.5">{TEXT_COMMON.LOGO_ATTACHED}</span>
        </div>

        <fieldset>
          <div className="mt-8">
            <label htmlFor="email" className="block text-base text-black/85">
              {TEXT_LOGIN.EMAIL_ADDRESS}
            </label>
            <div className="mt-2">
              <Input
                id="email"
                name="email"
                type="text"
                value={form.values.email}
                onChange={form.handleChange}
                aria-invalid={form.email ? 'false' : 'true'}
                invalid={form.errors.email}
              />

              <p className="text-sm text-danger-229 max-w-100">
                {!form.values.email && form.errors.email && <ErrorMessage>{MESSAGE_COMMON.NOT_EMPTY}</ErrorMessage>}
                {form.values.email ? (
                  emailInValidJapan ? (
                    <ErrorMessage>{MESSAGE_LOGIN.EMAIL_INVALID_FORMAT}</ErrorMessage>
                  ) : (
                    form.errors.email && <ErrorMessage>{MESSAGE_LOGIN.INVALID_EMAIL}</ErrorMessage>
                  )
                ) : (
                  <></>
                )}
              </p>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <div className="mt-4 relative">
            <label htmlFor="password" className="block text-base text-black/85">
              {TEXT_LOGIN.PASSWORD}
            </label>
            <div className="mt-2">
              <Input
                id="password"
                name="password"
                type="password"
                value={form.values.password}
                onChange={form.handleChange}
                invalid={form.errors.password}
              />
              {form.errors.password && (
                <p className="text-sm text-danger-229">
                  <ErrorMessage>{MESSAGE_COMMON.NOT_EMPTY}</ErrorMessage>
                </p>
              )}
            </div>
          </div>
        </fieldset>

        <div className="mt-4 flex justify-center">
          {isLoading ? (
            <span className="text-sm border-b border-primary-339 text-primary-339">{TEXT_LOGIN.FORGET_PASSWORD_LINK_TEXT}</span>
          ) : (
            <Link
              onClick={removeFromLocalStorage(LOCAL_STORAGE_KEYS.SEND_EMAIL_FORGOT_PASSWORD)}
              to={AppRoutes.resetPassword}
              className="text-sm border-b border-primary-339 text-primary-339"
            >
              {TEXT_LOGIN.FORGET_PASSWORD_LINK_TEXT}
            </Link>
          )}
        </div>
        <Button
          type={isLoading ? 'button' : 'submit'}
          text={TEXT_LOGIN.LOGIN}
          className={`mx-auto mt-8 py-2 w-60 h-10 border border-solid border-primary-423 shadow-subtle-4 ${isLoading && 'opacity-70'}`}
          color="primary"
        />
        <Button
          type={isLoading ? 'button' : 'link'}
          to={AppRoutes.register}
          text={TEXT_LOGIN.REGISTER_LINK_TEXT}
          className="mx-auto mt-6 py-2 w-60 h-10 shadow-subtle-2 text-black/85"
          color="outline"
        />
      </form>
    </main>
  );
};

export default React.memo(Login);
